import React, { useState, useEffect, useRef, useCallback } from 'react'
import Header from './components/Header'
import Content from './components/Content'
import AnimatedBackground from './components/AnimatedBackground'

function App() {
    const [theme, setTheme] = useState('light')
    const [searchTerm, setSearchTerm] = useState('')
    const [isScrolled, setIsScrolled] = useState(false)
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
    const [isPrinting, setIsPrinting] = useState(false)

    const appRef = useRef(null)
    const contentScrollRef = useRef(null)
    const scrollProgressRef = useRef(0)
    const isTransitioning = useRef(false)

    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark')
    }, [theme])

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light')
    }

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        setTheme(mediaQuery.matches ? 'dark' : 'light')
        const handleChange = (e) => {
            setTheme(e.matches ? 'dark' : 'light')
        }
        mediaQuery.addListener(handleChange)
        return () => mediaQuery.removeListener(handleChange)
    }, [])

    useEffect(() => {
        const mediaQueryList = window.matchMedia('print')
        const listener = (mql) => {
            setIsPrinting(mql.matches)
        }
        mediaQueryList.addListener(listener)
        return () => mediaQueryList.removeListener(listener)
    }, [])

    const setScrolledState = useCallback((value) => {
        if (isTransitioning.current) {
            return
        }
        isTransitioning.current = true
        setIsScrolled(value)
        scrollProgressRef.current = value ? 1 : 0
        setTimeout(() => {
            isTransitioning.current = false
        }, 500)
    }, [])

    useEffect(() => {
        const app = appRef.current
        if (!app) {
            return
        }

        const handleWheel = (e) => {
            if (isTransitioning.current) {
                return
            }
            if (!isScrolled && e.deltaY > 0) {
                setScrolledState(true)
            } else if (isScrolled && e.deltaY < 0) {
                const contentScroll = contentScrollRef.current
                if (contentScroll && contentScroll.scrollTop <= 0) {
                    setScrolledState(false)
                }
            }
        }

        app.addEventListener('wheel', handleWheel, { passive: true })
        return () => {
            app.removeEventListener('wheel', handleWheel)
        }
    }, [isScrolled, setScrolledState])

    useEffect(() => {
        const app = appRef.current
        if (!app) {
            return
        }

        let touchStartY = 0

        const handleTouchStart = (e) => {
            touchStartY = e.touches[0].clientY
        }

        const handleTouchMove = (e) => {
            if (isTransitioning.current) {
                return
            }
            const deltaY = touchStartY - e.touches[0].clientY

            if (!isScrolled && deltaY > 50) {
                setScrolledState(true)
            } else if (isScrolled && deltaY < -50) {
                const contentScroll = contentScrollRef.current
                if (contentScroll && contentScroll.scrollTop <= 0) {
                    setScrolledState(false)
                }
            }
        }

        app.addEventListener('touchstart', handleTouchStart, { passive: true })
        app.addEventListener('touchmove', handleTouchMove, { passive: true })
        return () => {
            app.removeEventListener('touchstart', handleTouchStart)
            app.removeEventListener('touchmove', handleTouchMove)
        }
    }, [isScrolled, setScrolledState])

    const handleSearchTerm = useCallback((term) => {
        if (term.length === 1 && !isScrolled) {
            setScrolledState(true)
        }
        setSearchTerm(term)
        if (term.length > 2 && contentScrollRef.current) {
            setTimeout(() => {
                if (contentScrollRef.current) {
                    contentScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
                }
            }, 200)
        }
    }, [isScrolled, setScrolledState])

    useEffect(() => {
        const detectKeyboard = () => {
            const newVisibleHeight = window.visualViewport.height
            const isKeyboard = window.innerHeight > newVisibleHeight
            setIsKeyboardVisible(isKeyboard)

            if (appRef.current) {
                appRef.current.style.height = `${newVisibleHeight}px`
            }
        }

        window.visualViewport.addEventListener('resize', detectKeyboard)
        window.visualViewport.addEventListener('scroll', detectKeyboard)

        return () => {
            window.visualViewport.removeEventListener('resize', detectKeyboard)
            window.visualViewport.removeEventListener('scroll', detectKeyboard)
        }
    }, [])

    return (
        <div ref={appRef} className='h-dvh flex flex-col overflow-hidden print:h-auto print:overflow-visible'>
            <Header
                isScrolled={isScrolled}
                isKeyboardVisible={isKeyboardVisible}
                toggleTheme={toggleTheme}
                setSearchTerm={handleSearchTerm}
                onScrollDown={() => setScrolledState(true)}
            />
            <div className='content-scroll' ref={contentScrollRef}>
                <div className='mx-auto max-w-7xl w-full'>
                    <Content
                        isPrinting={isPrinting}
                        className='flex-1 mx-4'
                        searchTerm={searchTerm}
                    />
                </div>
            </div>
            <AnimatedBackground scrollProgressRef={scrollProgressRef} theme={theme} />
        </div>
    )
}

export default App
